import { useState } from "react";
import { useForm } from "react-hook-form";
import { FcGoogle } from "react-icons/fc";
import {
  HiEye,
  HiEyeOff,
  HiLockClosed,
  HiMail,
  HiUserCircle,
} from "react-icons/hi";
import { Link } from "react-router-dom";

import { LoginCredentials } from "@/types/auth";

interface LoginFormProps {
  error?: string;
  isLoading?: boolean;
  onGoogleLogin: () => void;
  onSubmit: (data: LoginCredentials) => void;
}

export default function LoginForm({
  error,
  isLoading = false,
  onGoogleLogin,
  onSubmit,
}: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<LoginCredentials>();

  const handleFormSubmit = (data: LoginCredentials) => {
    onSubmit(data);
  };

  return (
    <div className="card w-full max-w-md bg-base-100 shadow-xl">
      <div className="card-body">
        {/* Header */}
        <button
          aria-label="Go Home"
          className="inline-block rounded-md bg-white bg-opacity-20 dark:bg-white dark:bg-opacity-10 text-gray-900 dark:text-gray-100 px-6 py-3 font-semibold backdrop-blur-sm transition hover:bg-opacity-40 dark:hover:bg-opacity-20"
          onClick={() => (window.location.href = "/")}
          type="button"
        >
          Go Home
        </button>
        <div className="mb-6 text-center">
          <div className="mb-4 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary">
              <HiUserCircle className="h-10 w-10 text-primary-content" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-base-content">Welcome Back</h2>
          <p className="mt-2 text-base-content/70">
            Sign in to access your dashboard
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="alert alert-error mb-4">
            <span className="whitespace-pre-line text-sm">{error}</span>
          </div>
        )}

        {/* Google Login Button */}
        {/* <button
          className="btn btn-outline mb-4 w-full normal-case"
          disabled={isLoading}
          onClick={onGoogleLogin}
          type="button"
        >
          <FcGoogle className="mr-2 h-5 w-5" />
          Continue with Google
        </button> */}

        {/* <div className="divider">or</div> */}

        {/* Login Form */}
        <form className="space-y-4" onSubmit={handleSubmit(handleFormSubmit)}>
          {/* Email Field */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">Email</span>
            </label>
            <div className="relative">
              <input
                {...register("email", {
                  pattern: {
                    message: "Invalid email address",
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  },
                  required: "Email is required",
                })}
                className={`input input-bordered w-full pl-10 ${
                  errors.email ? "input-error" : ""
                }`}
                disabled={isLoading}
                placeholder="Enter your email"
                type="email"
              />
              <HiMail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-base-content/50" />
            </div>
            {errors.email && (
              <label className="label">
                <span className="label-text-alt text-error">
                  {errors.email.message}
                </span>
              </label>
            )}
          </div>

          {/* Password Field */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">Password</span>
              <Link
                className="link label-text-alt link-primary"
                to="/auth/forgot-password"
              >
                Forgot password?
              </Link>
            </label>
            <div className="relative">
              <input
                {...register("password", {
                  minLength: {
                    message: "Password must be at least 6 characters",
                    value: 6,
                  },
                  required: "Password is required",
                })}
                className={`input input-bordered w-full pl-10 pr-10 ${
                  errors.password ? "input-error" : ""
                }`}
                disabled={isLoading}
                placeholder="Enter your password"
                type={showPassword ? "text" : "password"}
              />
              <HiLockClosed className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-base-content/50" />
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 transform"
                disabled={isLoading}
                onClick={() => setShowPassword(!showPassword)}
                type="button"
              >
                {showPassword ? (
                  <HiEyeOff className="h-5 w-5 text-base-content/50" />
                ) : (
                  <HiEye className="h-5 w-5 text-base-content/50" />
                )}
              </button>
            </div>
            {errors.password && (
              <label className="label">
                <span className="label-text-alt text-error">
                  {errors.password.message}
                </span>
              </label>
            )}
          </div>

          {/* Remember Me */}
          <div className="form-control">
            <label className="label cursor-pointer justify-start">
              <input
                {...register("rememberMe")}
                className="checkbox-primary checkbox mr-3"
                disabled={isLoading}
                type="checkbox"
              />
              <span className="label-text">Remember me</span>
            </label>
          </div>

          {/* Submit Button */}
          <button
            className="btn btn-primary w-full"
            disabled={isLoading}
            type="submit"
          >
            {isLoading ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Signing in...
              </>
            ) : (
              "Sign In"
            )}
          </button>
        </form>

        {/* Register Link
        <div className="mt-6 text-center">
          <p className="text-base-content/70">
            Do not have an account?{" "}
            <Link
              className="link link-primary font-semibold"
              to="/auth/register"
            >
              Create one
            </Link>
          </p>
        </div> */}
      </div>
    </div>
  );
}
